'use client';

import type { ConvertedExhibitionProductWidgetData } from '@weberus/shared-types';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { yaNative } from '@weberus/ui/utils/ya-native';
import { RectangleButton } from '@yanolja-rnd/nol-design-system';
import { IcArrowforwardFill } from '@yanolja-rnd/nol-icons';
import { VerticalProductItem } from '@weberus/ui/components';
import { YaTrackerImpression, clientLogStandardized } from '@weberus/client-tracker';
import { BaseImage } from '@/app/_components/common/base-image';

interface SwiperItemProps {
  item: ConvertedExhibitionProductWidgetData['items'][0];
  pageNameLog: HomeLogPageNameValue;
}

export default function SwiperItem({ item, pageNameLog }: SwiperItemProps) {
  const handleClickDetails = () => {
    clientLogStandardized('click', pageNameLog, item.logMeta);

    if (!item.header?.more?.action) {
      return;
    }
    if (yaNative.isEnabled) {
      yaNative.deepLink({ link: item.header.more.action.app });
    } else {
      location.href = item.header.more.action.web;
    }
  };

  if (item.items.length < 3) {
    return null;
  }

  return (
    <div className="relative">
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-12">
        <BaseImage src={item.backgroundImageUrl} alt="" className="object-cover" />
      </div>
      <div className="absolute left-0 top-0 h-full w-full rounded-12 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.5)_0%,rgba(0,0,0,0.1)_20%,rgba(255,255,255,0)_40%,rgba(255,255,255,0.9)_80%)]" />
      <div className="absolute top-0 mx-24 mt-32 h-[9.6rem]">
        {!!item.header?.title && (
          <h2 className="text-static-white typography-subtitle-20-bold-line-clamp-2">{item.header.title}</h2>
        )}
        {!!item.header?.more && (
          <div className="mt-16">
            <YaTrackerImpression onImpressed={() => clientLogStandardized('impression', pageNameLog, item.logMeta)}>
              <RectangleButton
                theme="filled_basic"
                icon={<IcArrowforwardFill />}
                weight="bold"
                direction="right"
                size={{ mobile: 'small' }}
                className="min-w-64 py-0"
                isCapsule
                onClick={handleClickDetails}>
                <span className="pl-6">{item.header.more.title}</span>
              </RectangleButton>
            </YaTrackerImpression>
          </div>
        )}
      </div>
      <div className="absolute bottom-20 left-16 flex w-[calc(100%-32px)]">
        <div className="flex w-full gap-8">
          {item.items?.map(({ productTapProps, logMeta }) => (
            <div
              key={productTapProps.id}
              className="w-1/3"
              onClick={() => clientLogStandardized('click', pageNameLog, logMeta)}>
              <div aria-hidden>
                <YaTrackerImpression onImpressed={() => clientLogStandardized('impression', pageNameLog, logMeta)}>
                  <div />
                </YaTrackerImpression>
              </div>
              <VerticalProductItem
                key={productTapProps.id}
                {...productTapProps}
                titleClassName="whitespace-normal max-h-32"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
